import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, canBackupRead, canCampaignsRead, canCasesRead, canManageMasters, canManageTelephony, canProcure, canQC, canRental, canInventoryRead, canSalesRead, canSystemMaintenance, canVendorReturnsRead, canWarrantyRead } from "@/lib/auth";
import { LogOut, Layers, Sliders, Phone, Headphones, Truck, PackagePlus, ClipboardCheck, Warehouse, Receipt, DatabaseBackup, LifeBuoy, ShieldCheck, Megaphone, LayoutDashboard, Search, FileText, Settings as Cog, ShieldAlert, RotateCcw } from "lucide-react";

export default function AdminShell({ onOpenSearch }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", group: "overview", visible: () => true },
    { to: "/admin/purchases", label: "Purchases", icon: PackagePlus, testId: "nav-purchases", group: "procurement", visible: () => true },
    { to: "/admin/vendors", label: "Vendors", icon: Truck, testId: "nav-vendors", group: "procurement", visible: () => true },
    { to: "/admin/qc/jobs", label: "QC Jobs", icon: ClipboardCheck, testId: "nav-qc-jobs", group: "qc", visible: canQC },
    { to: "/admin/inventory", label: "Inventory", icon: Warehouse, testId: "nav-inventory", group: "warehouse", visible: canInventoryRead },
    { to: "/admin/vendor-returns", label: "Vendor Returns", icon: RotateCcw, testId: "nav-vendor-returns", group: "warehouse", visible: canVendorReturnsRead },
    { to: "/admin/rental", label: "Rental", icon: Warehouse, testId: "nav-rental", group: "warehouse", visible: canRental },
    { to: "/admin/sales", label: "Sales", icon: Receipt, testId: "nav-sales", group: "sales", visible: canSalesRead },
    { to: "/admin/cases", label: "Customer Cases", icon: LifeBuoy, testId: "nav-cases", group: "support", visible: canCasesRead },
    { to: "/admin/warranties", label: "Warranties", icon: ShieldCheck, testId: "nav-warranties", group: "support", visible: canWarrantyRead },
    { to: "/admin/campaigns", label: "Renewal Campaigns", icon: Megaphone, testId: "nav-campaigns", group: "support", visible: canCampaignsRead },
    { to: "/admin/call-queue", label: "AI Call Queue", icon: Headphones, testId: "nav-call-queue", group: "engine", visible: canManageMasters },
    { to: "/admin/reports", label: "Reports", icon: FileText, testId: "nav-reports", group: "insights", visible: canSalesRead },
    { to: "/admin/warranty-eligibility-rules", label: "Eligibility Rules", icon: Layers, testId: "nav-eligibility", group: "masters", visible: canManageMasters },
    { to: "/admin/warranty-pricing-rules", label: "Pricing Rules", icon: Sliders, testId: "nav-pricing", group: "masters", visible: canManageMasters },
    { to: "/admin/telephony-providers", label: "Telephony Providers", icon: Phone, testId: "nav-telephony", group: "masters", visible: canManageTelephony },
    { to: "/admin/settings", label: "Settings", icon: Cog, testId: "nav-settings", group: "system", visible: canManageMasters },
    { to: "/admin/backups", label: "Backup & Restore", icon: DatabaseBackup, testId: "nav-backup", group: "system", visible: canBackupRead },
    { to: "/admin/system-maintenance", label: "System Maintenance", icon: ShieldAlert, testId: "nav-system-maintenance", group: "system", visible: canSystemMaintenance },
  ];

  // Flat sidebar: preserve per-item visibility + the special procurement-group role gate.
  // V2.0 — All operational roles see the Procurement group.
  const OPS_ROLES = ["OPERATIONS_ENGINEER", "QC_ENGINEER", "PREPARATION_ENGINEER", "BATTERY_ENGINEER", "WAREHOUSE"];
  const isProcurementAllowed = (u) =>
    canProcure(u) || OPS_ROLES.includes(u?.role) || u?.role === "SALES" || u?.role === "ACCOUNTS";
  const visibleItems = navItems.filter((n) => {
    if (!n.visible(user)) return false;
    if (n.group === "procurement" && !isProcurementAllowed(user)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 w-64 bg-slate-900 text-slate-100 flex flex-col">
        <div className="px-6 py-5 border-b border-slate-800">
          <Link to="/admin" className="block" data-testid="brand-link">
            <div className="text-lg font-semibold tracking-tight">RentOPC OS</div>
            <div className="text-xs text-slate-400">Enterprise Operations Console</div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleItems.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} data-testid={n.testId} end={n.to === "/admin/dashboard"}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm ${isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60"}`}>
                <Icon size={16} /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800">
          <button onClick={onOpenSearch} data-testid="global-search-open"
            className="w-full inline-flex items-center justify-between gap-2 rounded-md bg-slate-800 hover:bg-slate-700 px-3 py-2 text-sm">
            <span className="inline-flex items-center gap-2 text-slate-200">
              <Search size={14} /> Search
            </span>
            <kbd className="text-[10px] text-slate-400 border border-slate-700 rounded px-1">⌘K</kbd>
          </button>
          <div className="text-sm truncate mt-3" data-testid="user-name">{user?.full_name}</div>
          <div className="text-xs text-slate-400 truncate" data-testid="user-role">{user?.role}</div>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md bg-slate-800 hover:bg-slate-700 px-3 py-2 text-sm">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>
      <main className="pl-64">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
